import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NoSymbolIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { apiService } from '../../services/api';
import { ApiKey, ApiScope } from '../../types';
import ConfirmDialog from '../ConfirmDialog';
import { EmptyRow, SortableTh, SortOrder, TableCard, TableHeader, TableViewport, usePageSize, thClass, tdClass } from '../table/TableKit';
import RelativeTime from '../RelativeTime';
import CopyIconButton from '../CopyIconButton';
import { urlEnum, urlPositiveInt, useUrlState } from '../../hooks/useUrlState';
import { usePageClamp } from '../../hooks/usePageClamp';
import { maskApiKey } from '../../utils/apiKey';

const SCOPE_OPTIONS: { value: ApiScope; description: string }[] = [
    { value: 'cli:analyze', description: 'Analyze locally via the Alchemist CLI' },
    { value: 'cli:convert', description: 'Analyze and convert locally via the Alchemist CLI' },
    { value: 'api:convert', description: 'Convert via the Alchemist Live API' },
];

const URL_SPEC = {
    sort: urlEnum(['name', 'api_key_prefix', 'is_active', 'created_at', 'last_used_at'] as const, 'created_at'),
    order: urlEnum(['asc', 'desc'] as const, 'desc'),
    page: urlPositiveInt(),
};

function ApiKeysTab() {
    const [pageSize, setPageSize] = usePageSize('user-api-keys');
    const [state, setUrlState] = useUrlState(URL_SPEC);
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const [total, setTotal] = useState(0);
    const onSort = (sort: string, order: SortOrder) =>
        setUrlState({ sort: sort as typeof state.sort, order, page: 1 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // Bumped on every request, so a slow old response cannot win.
    const generation = useRef(0);

    const [showCreate, setShowCreate] = useState(false);
    const [name, setName] = useState('');
    const [scope, setScope] = useState<ApiScope | null>(null);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    const [revealedApiKey, setRevealedApiKey] = useState<string | null>(null);

    const [confirmRevokeId, setConfirmRevokeId] = useState<number | null>(null);
    const [revoking, setRevoking] = useState(false);
    const [revokeError, setRevokeError] = useState<string | null>(null);

    const fetchApiKeys = useCallback(async () => {
        const gen = ++generation.current;
        setLoading(true);
        try {
            const res = await apiService.listApiKeys({
                limit: pageSize,
                offset: (state.page - 1) * pageSize,
                sort: state.sort,
                order: state.order,
            });
            if (gen !== generation.current) return;
            setApiKeys(res.data.api_keys);
            setTotal(res.data.total ?? res.data.api_keys.length);
            setError(null);
        } catch {
            if (gen !== generation.current) return;
            setApiKeys([]);
            setTotal(0);
            setError('Failed to load API keys.');
        } finally {
            if (gen === generation.current) setLoading(false);
        }
    }, [pageSize, state.order, state.page, state.sort]);

    useEffect(() => {
        fetchApiKeys();
    }, [fetchApiKeys]);

    usePageClamp({
        page: state.page,
        pageSize,
        total,
        ready: !loading && !error,
        setPage: (page) => setUrlState({ page }),
    });

    const handleCreate = async () => {
        if (!scope) return;
        setCreating(true);
        setCreateError(null);
        try {
            const res = await apiService.createApiKey(name.trim(), [scope]);
            setRevealedApiKey(res.data.api_key);
            setName('');
            setScope(null);
            await fetchApiKeys();
        } catch (e: any) {
            setCreateError(e?.response?.data?.message || 'Failed to create API key.');
        } finally {
            setCreating(false);
        }
    };

    const closeCreate = () => {
        setShowCreate(false);
        setRevealedApiKey(null);
        setCreateError(null);
        setName('');
        setScope(null);
    };

    const handleRevoke = async (id: number) => {
        setRevoking(true);
        setRevokeError(null);
        try {
            await apiService.revokeApiKey(id);
            // Close only after the refresh so the pending dialog blocks
            // a second revoke against the stale list
            await fetchApiKeys();
            setConfirmRevokeId(null);
        } catch {
            setRevokeError('Failed to revoke API key.');
        } finally {
            setRevoking(false);
        }
    };

    const closeRevokeConfirm = () => {
        setConfirmRevokeId(null);
        setRevokeError(null);
    };

    const apiKeyToRevoke = apiKeys.find((k) => k.id === confirmRevokeId);

    return (
        <div>
            {error && (
                <div className="mb-4 bg-red-600/20 text-red-400 px-4 py-2 rounded text-sm">
                    {error}
                </div>
            )}

            <TableCard>
                <TableHeader
                    title="API Keys"
                    pagination={{
                        offset: (state.page - 1) * pageSize,
                        limit: pageSize,
                        total,
                        onOffsetChange: (offset) => setUrlState({ page: offset / pageSize + 1 }),
                        onLimitChange: (size) => {
                            setPageSize(size);
                            setUrlState({ page: 1 });
                        },
                    }}
                >
                    <button
                        className="btn-primary text-sm px-3 py-1.5"
                        onClick={() => setShowCreate(true)}
                    >
                        Create API Key
                    </button>
                </TableHeader>
                <TableViewport className={loading ? "opacity-50 transition-opacity" : ""}>
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-700">
                                <SortableTh label="Name" sortKey="name" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                                <SortableTh label="API Key" sortKey="api_key_prefix" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                                <th className={thClass}>Scopes</th>
                                <SortableTh label="Status" sortKey="is_active" sort={state.sort} order={state.order} onSort={onSort} />
                                <SortableTh label="Created" sortKey="created_at" sort={state.sort} order={state.order} onSort={onSort} />
                                <SortableTh label="Last Used" sortKey="last_used_at" sort={state.sort} order={state.order} onSort={onSort} />
                                <th className={thClass}>Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                            {apiKeys.map((apiKey) => (
                                <tr key={apiKey.id}>
                                    <td className={tdClass}>{apiKey.name}</td>
                                    <td className={`${tdClass} font-mono`}>
                                        {maskApiKey(apiKey)}
                                    </td>
                                    <td className={tdClass}>
                                        {apiKey.scopes.map((s) => (
                                            <span
                                                key={s}
                                                className="bg-primary/20 text-primary px-2 py-1 rounded text-xs font-medium mr-1"
                                            >
                                                {s}
                                            </span>
                                        ))}
                                    </td>
                                    <td className={tdClass}>
                                        {apiKey.is_active ? (
                                            <span className="bg-primary/20 text-primary px-2 py-1 rounded text-xs font-medium">Active</span>
                                        ) : (
                                            <span className="bg-red-600/20 text-red-400 px-2 py-1 rounded text-xs font-medium">Revoked</span>
                                        )}
                                    </td>
                                    <td className={tdClass}>
                                        <RelativeTime value={apiKey.created_at} />
                                    </td>
                                    <td className={tdClass}>
                                        <RelativeTime value={apiKey.last_used_at} empty="Never" />
                                    </td>
                                    <td className={tdClass}>
                                        {apiKey.is_active && (
                                            <button
                                                className="btn-icon p-1 text-red-400 hover:bg-red-500/10"
                                                title="Revoke"
                                                aria-label="Revoke"
                                                onClick={() => {
                                                    setRevokeError(null);
                                                    setConfirmRevokeId(apiKey.id);
                                                }}
                                            >
                                                <NoSymbolIcon className="w-4 h-4" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {apiKeys.length === 0 && (
                                <EmptyRow colSpan={7} text={loading ? 'Loading...' : 'No API keys yet. Create one.'} />
                            )}
                        </tbody>
                    </table>
                </TableViewport>
            </TableCard>

            <ConfirmDialog
                open={confirmRevokeId !== null}
                title="Revoke API Key"
                message={
                    apiKeyToRevoke
                        ? `Revoke "${apiKeyToRevoke.name}"? Anything that uses this API key loses access at once.`
                        : 'Revoke this API key? Anything that uses it loses access at once.'
                }
                confirmLabel="Revoke Key"
                danger
                pending={revoking}
                error={revokeError}
                onConfirm={() => confirmRevokeId !== null && handleRevoke(confirmRevokeId)}
                onCancel={closeRevokeConfirm}
            />

            {showCreate && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
                    <div className="bg-widget-bg rounded-lg p-8 max-w-md w-full mx-4 border border-secondary shadow-xl">
                        {!revealedApiKey ? (
                            <>
                                <div className="flex justify-between items-start mb-4">
                                    <h3 className="text-lg font-semibold text-heading">Create API Key</h3>
                                    <button
                                        onClick={closeCreate}
                                        disabled={creating}
                                        aria-label="Close"
                                        className="text-body-color hover:text-heading transition-colors p-1 hover:bg-body-bg/50 rounded"
                                    >
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </div>
                                {createError && (
                                    <div className="mb-4 bg-red-600/20 text-red-400 px-4 py-2 rounded text-sm">
                                        {createError}
                                    </div>
                                )}
                                <label className="block text-sm text-secondary mb-1" htmlFor="api-key-name">
                                    Name
                                </label>
                                <input
                                    id="api-key-name"
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="For example: data engineering team"
                                    className="w-full bg-body-bg border border-gray-700 rounded px-3 py-2 text-body-color mb-4"
                                />
                                <div className="text-sm text-secondary mb-2">Scope</div>
                                <div className="flex flex-col gap-2 mb-6">
                                    {SCOPE_OPTIONS.map((option) => (
                                        <label
                                            key={option.value}
                                            className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                                                scope === option.value
                                                    ? 'border-primary bg-primary/10'
                                                    : 'border-gray-700 hover:border-gray-500'
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="api-key-scope"
                                                className="mt-0.5"
                                                checked={scope === option.value}
                                                onChange={() => setScope(option.value)}
                                            />
                                            <span className="flex flex-col gap-0.5">
                                                <span className="font-mono text-sm text-body-color">{option.value}</span>
                                                <span className="text-xs text-secondary">{option.description}</span>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button className="btn-secondary" onClick={closeCreate} disabled={creating}>
                                        Cancel
                                    </button>
                                    <button
                                        className="btn-primary"
                                        onClick={handleCreate}
                                        disabled={creating || !name.trim() || !scope}
                                    >
                                        {creating ? 'Creating...' : 'Create'}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="flex justify-between items-start mb-4">
                                    <h3 className="text-lg font-semibold text-heading">API Key Created</h3>
                                    <button
                                        onClick={closeCreate}
                                        aria-label="Close"
                                        className="text-body-color hover:text-heading transition-colors p-1 hover:bg-body-bg/50 rounded"
                                    >
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </div>
                                <div className="bg-warning/10 border border-warning/40 rounded p-4 mb-6">
                                    <p className="text-warning text-sm font-medium mb-2">
                                        Copy this API key now. You cannot view it again.
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            readOnly
                                            value={revealedApiKey}
                                            className="flex-1 bg-body-bg border border-gray-700 rounded px-3 py-2 text-body-color font-mono text-xs"
                                        />
                                        <CopyIconButton text={revealedApiKey} label="API key" className="btn-icon" iconClassName="w-5 h-5" />
                                    </div>
                                </div>
                                <div className="flex justify-end">
                                    <button className="btn-secondary" onClick={closeCreate}>
                                        Done
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ApiKeysTab;
